import { useMsal } from '@azure/msal-react';
import { CloseEvent, DropdownChangeEvent } from '@volvo/vcdk';
import {
  Drawer,
  DrawerContent,
  DrawerTopBar,
  List,
  ListItemAppendix,
  ListItemContent,
  ListItemInteractive,
  SystemIcon,
} from '@volvo/vcdk-react';
import { Dropdown } from '@volvo/vcdk-react/Dropdown';
import { DropdownOption } from '@volvo/vcdk-react/DropdownOption';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { loginRequest, msalSettings } from '@/config/MsalAuthConfig';
import { clearNativeTokens, isNativeWebview } from '@/config/nativeTokenStore';
import { queryClient } from '@/config/QueryConfig';
import { analyticsEvents } from '@/contexts/analytics/events';
import { EnvironmentContext } from '@/contexts/environment';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import useMediaQuery from '@/hooks/useMediaQuery';

type Props = {
  isOpen: boolean;
  handleOnToggle: () => void;
};

const UserMenu: React.FC<Props> = ({ isOpen, handleOnToggle }) => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const { mediaQueryNodes: mq } = useMediaQuery();
  const { fleetAccountId, fleetAccounts, setFleetAccountId } = useFleetAccount();
  const { nativeAction } = useContext(EnvironmentContext);
  const MsalAccount = instance.getActiveAccount();

  const handleEditProfile = () => {
    if (isNativeWebview()) {
      nativeAction.named.events.editProfile();
      return;
    }
    instance.loginRedirect({
      ...loginRequest,
      authority: msalSettings.authorities.editProfile,
    });
  };

  const handleLogout = async () => {
    // Clear storage
    localStorage.clear();
    sessionStorage.clear();

    // Clear Service Worker caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((cache) => caches.delete(cache)));
    }

    if (isNativeWebview()) {
      clearNativeTokens();
      queryClient.clear();
      await instance.clearCache();
      window.dispatchEvent(new Event('vpc-session-reset'));
      nativeAction.named.events.logout();
      return;
    }

    instance.logoutRedirect({
      account: MsalAccount,
    });
  };

  const onChange = (e: DropdownChangeEvent) => {
    const newValue = e.detail.value;
    setFleetAccountId(newValue);
  };

  useEffect(() => {
    const onVcdkClose = () => handleOnToggle();
    window.addEventListener(CloseEvent.eventName, onVcdkClose);
    return () => window.removeEventListener(CloseEvent.eventName, onVcdkClose);
  }, [handleOnToggle]);

  const menuMode = mq({
    default: 'modal' as const,
    sm: 'permanent' as const,
  });

  return (
    <Drawer
      open={isOpen}
      width="376px"
      side="end"
      mode={menuMode}
      onClose={() => handleOnToggle()}
      className="absolute top-0 right-0 bottom-0 z-30"
    >
      <DrawerTopBar heading={t(($) => $.components.user_menu.title)} />
      <DrawerContent>
        <div className="flex flex-col gap-8">
          <List>
            <ListItemInteractive
              onClick={() => {
                analyticsEvents.Interaction.componentInteraction({
                  parent: 'UserMenu',
                  type: 'ListItemInteractive',
                  title: 'Account Settings',
                  href: '/account-settings',
                  placement: 'UserMenu',
                });
                handleEditProfile();
              }}
            >
              <ListItemAppendix>
                <SystemIcon icon="user" />
              </ListItemAppendix>
              <ListItemContent>{t(($) => $.components.user_menu.account_settings)}</ListItemContent>
            </ListItemInteractive>
            <ListItemInteractive
              onClick={() => {
                analyticsEvents.Interaction.componentInteraction({
                  parent: 'UserMenu',
                  type: 'ListItemInteractive',
                  title: 'Logout',
                  href: '/logout',
                  placement: 'UserMenu',
                });
                handleLogout();
              }}
            >
              <ListItemAppendix>
                <SystemIcon icon="logout" />
              </ListItemAppendix>
              <ListItemContent>{t(($) => $.components.user_menu.logout)}</ListItemContent>
            </ListItemInteractive>
          </List>
          <Dropdown
            label={t(($) => $.components.user_menu.switch_fleet_account)}
            onChange={onChange}
            size="medium"
            className="[&::part(input-wrapper)]:h-10"
            floatingLabel={false}
            value={fleetAccountId || ''}
          >
            {fleetAccounts?.map((option) => {
              return (
                <DropdownOption key={option.fleetAccountId} value={option.fleetAccountId}>
                  {option.name}
                </DropdownOption>
              );
            })}
          </Dropdown>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default UserMenu;
